// Parking clock. The expiry is an absolute timestamp, so it survives reloads,
// time-zone changes and the app being closed. Stored locally for the countdown
// and on the server so the reminder can be pushed while the app is shut.

import { api } from "./api";

const KEY = "spotdrop_parking_timer";

export const DURATION_PRESETS = [
  { label: "30 min", minutes: 30 },
  { label: "1 hour", minutes: 60 },
  { label: "90 min", minutes: 90 },
  { label: "2 hours", minutes: 120 },
  { label: "3 hours", minutes: 180 },
];

export const LEAD_OPTIONS = [5, 10, 15, 30];
export const DEFAULT_LEAD = 15;

export function readTimer() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const t = JSON.parse(raw);
    return t?.ends_at ? t : null;
  } catch {
    return null;
  }
}

function write(timer) {
  try {
    if (timer) localStorage.setItem(KEY, JSON.stringify(timer));
    else localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new Event("spotdrop-timer"));
  } catch {
    /* ignore */
  }
}

/** Positive seconds left, or a negative number once the time is up. */
export function secondsLeft(timer) {
  if (!timer?.ends_at) return null;
  return Math.round((new Date(timer.ends_at).getTime() - Date.now()) / 1000);
}

export function formatLeft(seconds) {
  if (seconds === null) return "";
  const over = seconds < 0;
  const s = Math.abs(seconds);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  let text;
  if (d > 0) text = `${d}d ${h}h`;
  else if (h > 0) text = `${h}h ${String(m).padStart(2, "0")}m`;
  else text = `${m}m ${String(s % 60).padStart(2, "0")}s`;
  return over ? `${text} ago` : text;
}

/** Minute-level label for the compact indicator (no ticking seconds). */
export function formatLeftShort(seconds) {
  if (seconds === null) return "";
  const s = Math.max(0, seconds);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${Math.max(1, m)}m`;
}

/** Local date/time string for an <input type="datetime-local"> in this time zone. */
export function toLocalInput(date) {
  const d = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 16);
}

export function formatEndsAt(timer) {
  if (!timer?.ends_at) return "";
  const d = new Date(timer.ends_at);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return d.toLocaleString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    ...(sameDay ? {} : { day: "numeric", month: "short" }),
  });
}

export async function startTimer({ endsAt, leadMinutes = DEFAULT_LEAD, label = null }) {
  const timer = { ends_at: new Date(endsAt).toISOString(), lead_minutes: leadMinutes, label };
  write(timer);
  try {
    const { data } = await api.put("/parking-timer", {
      ends_at: timer.ends_at,
      lead_minutes: leadMinutes,
      label,
    });
    if (data?.timer) write(data.timer);
    return { ...(data?.timer || timer), synced: true };
  } catch {
    // Offline or the server rejected it: the local countdown still works.
    return { ...timer, synced: false };
  }
}

export async function stopTimer() {
  write(null);
  try {
    await api.delete("/parking-timer");
  } catch {
    /* ignore */
  }
}

/** Server copy wins after a reinstall or a new browser. */
export async function syncTimer() {
  try {
    const { data } = await api.get("/parking-timer");
    if (data?.timer) {
      write(data.timer);
      return data.timer;
    }
    if (data && data.timer === null) {
      const local = readTimer();
      if (local && secondsLeft(local) > 0) return local;
      write(null);
      return null;
    }
  } catch {
    /* keep the local copy */
  }
  return readTimer();
}
